import React, { useMemo, useState } from 'react';
import type { BrandOperationsOrder } from '../../api/client';
import './brand-order-calendar.css';

const dateFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit' });
export function brandOrderDate(value?: string): string {
  if (!value || Number.isNaN(Date.parse(value))) return '';
  const parts = dateFormatter.formatToParts(new Date(value));
  return ['year', 'month', 'day'].map(type => parts.find(part => part.type === type)!.value).join('-');
}

export function brandOrderStatus(row: BrandOperationsOrder): string {
  const order = row.order;
  if (!order) return '其他';
  // Preserve review, trip progress and terminal states before classifying fulfillment.
  if (order.status && !['待付款', '待派車', '已派車'].includes(order.status)) return order.status;
  if (!order.paid) return '未付款';
  if (order.driverUid != null || order.status === '已派車') return '已派車';
  return row.sentAt ? '已派單' : '待派單';
}
const primaryStatuses = ['未付款', '待派單', '已派單', '已派車'];
const weekdays = ['日', '一', '二', '三', '四', '五', '六'];

export default function BrandOrderCalendar({ orders, renderOrder }: {
  orders: BrandOperationsOrder[];
  renderOrder: (row: BrandOperationsOrder) => React.ReactNode;
}) {
  const today = brandOrderDate(new Date().toISOString());
  const [month, setMonth] = useState(() => today.slice(0, 7));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const byDate = useMemo(() => {
    const grouped = new Map<string, BrandOperationsOrder[]>();
    orders.forEach(row => {
      if (!row.order) return;
      const date = brandOrderDate(row.order.rideTime);
      grouped.set(date, [...(grouped.get(date) || []), row]);
    });
    grouped.forEach(rows => rows.sort((a, b) => Date.parse(a.order!.rideTime!) - Date.parse(b.order!.rideTime!)));
    return grouped;
  }, [orders]);
  const [year, monthNumber] = month.split('-').map(Number);
  const firstWeekday = new Date(Date.UTC(year, monthNumber - 1, 1)).getUTCDay();
  const dayCount = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const cells = Math.ceil((firstWeekday + dayCount) / 7) * 7;
  const dayOrders = selectedDate === null ? [] : byDate.get(selectedDate) || [];
  const counts = new Map<string, number>(primaryStatuses.map(label => [label, 0]));
  dayOrders.forEach(row => { const label = brandOrderStatus(row); counts.set(label, (counts.get(label) || 0) + 1); });
  const selectDate = (date: string) => { setSelectedDate(date); setStatus(null); };
  const changeMonth = (offset: number) => {
    setMonth(new Date(Date.UTC(year, monthNumber - 1 + offset, 1)).toISOString().slice(0, 7));
    setSelectedDate(null); setStatus(null);
  };
  return <div className="brand-order-calendar">
    <div className="brand-calendar-toolbar">
      <h3>{year} 年 {monthNumber} 月</h3>
      <div className="brand-calendar-navigation">
        <button type="button" className="btn btn-outline-secondary" aria-label="上一個月" onClick={() => changeMonth(-1)}>‹</button>
        <button type="button" className="btn btn-outline-secondary" onClick={() => { setMonth(today.slice(0, 7)); selectDate(today); }}>今天</button>
        <button type="button" className="btn btn-outline-secondary" aria-label="下一個月" onClick={() => changeMonth(1)}>›</button>
      </div>
    </div>
    <p className="text-muted">依乘車日期（台北時間）顯示訂單數量，點選日期查看狀態與訂單。</p>
    <div className="brand-calendar-grid" aria-label={`${year} 年 ${monthNumber} 月訂單月曆`}>
      {weekdays.map(day => <div className="brand-calendar-weekday" key={day}>{day}</div>)}
      {Array.from({ length: cells }, (_, index) => {
        const day = index - firstWeekday + 1;
        if (day < 1 || day > dayCount) return <div className="brand-calendar-blank" key={index} aria-hidden="true" />;
        const date = `${month}-${String(day).padStart(2, '0')}`;
        const count = byDate.get(date)?.length || 0;
        return <button type="button" key={index} className="brand-calendar-day" aria-label={`${date}，${count} 筆訂單`}
          aria-pressed={selectedDate === date} aria-current={date === today ? 'date' : undefined} onClick={() => selectDate(date)}>
          <span>{day}</span>{count > 0 && <strong>{count}<span className="brand-calendar-count-label"> 筆訂單</span></strong>}
        </button>;
      })}
    </div>
    {!!byDate.get('')?.length && <button type="button" className="btn btn-outline-secondary mt-3" onClick={() => selectDate('')}>乘車日期待確認（{byDate.get('')!.length} 筆）</button>}
    {selectedDate === null ? <p className="brand-calendar-prompt">請選擇日期查看當日訂單。</p> : <section className="brand-calendar-details" aria-label="當日訂單">
      <h3>{selectedDate || '乘車日期待確認'} · {dayOrders.length} 筆訂單</h3>
      <div className="brand-calendar-statuses" aria-label="訂單狀態分類">
        <button type="button" className="btn btn-outline-secondary" aria-pressed={status === null} onClick={() => setStatus(null)}>全部 {dayOrders.length}</button>
        {Array.from(counts, ([label, count]) => <button type="button" className="btn btn-outline-secondary" key={label} aria-pressed={status === label} onClick={() => setStatus(label)}>{label} {count}</button>)}
      </div>
      <p className="text-muted">待派單：尚未轉派車隊 · 已派單：已轉派車隊 · 已派車：車隊已指派司機</p>
      {!dayOrders.length ? <p>當日尚無訂單。</p> : <>
        {status && !counts.get(status) && <p>此分類尚無訂單。</p>}
        {dayOrders.filter(row => !status || brandOrderStatus(row) === status).map(renderOrder)}
      </>}
    </section>}
  </div>;
}
